import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MapPin, Store, Search, Truck, Phone, MessageCircle, Globe, Flag } from "lucide-react";
import { ProductCard, VerifiedBadge } from "@/components/marketplace/shared";
import { Rating, ReviewsList } from "@/components/marketplace/Reviews";
import { MessageButton } from "@/components/messages/shared";

export function Sellers() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/sellers", { params: q ? { q } : {} }).then(r => setItems(r.data)), 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="page fade-up">
      <Link to="/marketplace" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Marketplace</Link>
      <h1 className="font-display text-2xl font-extrabold mb-3">Lojas & Vendedores</h1>
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input data-testid="sellers-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Procurar lojas…"
               className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm outline-none focus:border-black" />
      </div>
      {items?.length === 0 && <p data-testid="sellers-empty" className="text-sm text-zinc-500 text-center py-8">Ainda não há lojas aprovadas.</p>}
      <div className="space-y-2 mb-8">
        {items?.map(s => (
          <Link key={s.id} to={`/marketplace/loja/${s.id}`} data-testid={`seller-card-${s.id}`} className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-zinc-200 card-hover">
            <div className="w-12 h-12 rounded-xl bg-black text-white flex items-center justify-center overflow-hidden shrink-0">
              {s.logo_url ? <img src={s.logo_url} alt="" className="w-full h-full object-cover" /> : <Store size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate flex items-center gap-1">{s.shop_name}<VerifiedBadge verified={s.seller_verified} testid={`verified-badge-${s.id}`} /></div>
              <div className="text-[11px] text-zinc-500 truncate">{s.categories?.slice(0, 2).join(" · ")}</div>
              <div className="text-[11px] text-zinc-500 flex items-center gap-2"><span className="flex items-center gap-1"><MapPin size={10} /> {s.city}, {s.country}</span><Rating avg={s.rating_avg} count={s.rating_count} testid={`rating-${s.id}`} /></div>
            </div>
            {s.claimable && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">Por reivindicar</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function SellerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/sellers/${id}`).then(r => setS(r.data)).catch(() => setS(false)); }, [id]);
  if (s === null) return <div className="page pt-8 text-center text-zinc-500">A carregar…</div>;
  if (s === false) return <div className="page pt-8 text-center text-zinc-500">Loja não encontrada.</div>;

  const claim = async () => {
    try { await api.post(`/sellers/${s.id}/claim`); toast.success("Pedido enviado. A ANGOLAUK+ vai verificar a tua ligação a esta loja."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const c = s.contacts || {};
  return (
    <div className="page fade-up">
      <Link to="/marketplace/lojas" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Lojas</Link>
      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden mb-4">
        {s.photos?.[0] && <img src={s.photos[0]} alt="" className="w-full aspect-[16/7] object-cover" />}
        <div className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-14 h-14 rounded-xl bg-black text-white flex items-center justify-center overflow-hidden">
              {s.logo_url ? <img src={s.logo_url} alt="" className="w-full h-full object-cover" /> : <Store size={22} />}
            </div>
            <div className="flex-1 min-w-0">
              <h1 data-testid="seller-name" className="font-display text-xl font-extrabold leading-tight flex items-center gap-2 flex-wrap">{s.shop_name}<VerifiedBadge verified={s.seller_verified} size="lg" testid="seller-verified-badge" /></h1>
              <div className="text-xs text-zinc-500 flex items-center gap-1"><MapPin size={11} /> {s.city}{s.region ? `, ${s.region}` : ""} · {s.country}</div>
              <Rating avg={s.rating_avg} count={s.rating_count} size="lg" testid="seller-rating" />
            </div>
          </div>
          {s.status === "suspended" && <div className="mb-3 p-2 rounded-lg bg-red-50 text-red-700 text-xs font-semibold">Loja temporariamente suspensa.</div>}
          <div className="flex flex-wrap gap-1.5 mb-3">{s.categories?.map(x => <span key={x} className="chip !text-[11px]">{x}</span>)}</div>
          <p className="text-sm text-zinc-700 leading-relaxed mb-3 whitespace-pre-line">{s.description}</p>
          <div className="text-xs text-zinc-600 flex items-center gap-1 mb-3"><Truck size={12} /> {s.delivery_methods?.map(m => m === "delivery" ? "Entrega" : "Levantamento").join(" · ")}</div>
          <div className="grid grid-cols-2 gap-2">
            {c.phone && <a href={`tel:${c.phone}`} target="_blank" rel="noreferrer" className="cbtn"><Phone size={13} /> Ligar</a>}
            {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="cbtn"><MessageCircle size={13} /> WhatsApp</a>}
            {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="cbtn"><Globe size={13} /> Website</a>}
          </div>
          {s.claimable && !s.owner && user && (
            <button data-testid="seller-claim-btn" onClick={claim} className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-zinc-300 text-xs font-semibold hover:border-black">
              <Flag size={13} /> Esta loja é minha — reivindicar perfil
            </button>
          )}
        </div>
      </div>
      <h2 className="font-display font-bold text-base mb-2">Produtos ({s.products?.length || 0})</h2>
      {s.products?.length === 0 && <p className="text-sm text-zinc-500 text-center py-6">Sem produtos publicados.</p>}
      <div className="grid grid-cols-2 gap-3 mb-4">{s.products?.map(p => <ProductCard key={p.id} p={p} />)}</div>
      <div className="mb-3"><MessageButton context_type="seller" context_id={s.id} label="Mensagem à loja" className="w-full" /></div>
      <ReviewsList seller_id={s.id} title="Avaliações da loja" testid="seller-reviews" />
      <div className="h-8" />
      <style>{`.cbtn{display:flex;align-items:center;justify-content:center;gap:.5rem;padding:.6rem;border-radius:.75rem;background:#000;color:#fff;font-size:.75rem;font-weight:600}.cbtn:hover{background:#c8102e}`}</style>
    </div>
  );
}
